import { Divider, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SewCustomerParties } from '@/backoffice/features/tenants/components/sew-customer-parties/sew-customer-parties';
import {
  TUpdateTenantFormProps,
  TUpdateTenantFormSchema,
} from '@/backoffice/features/tenants/components/update-tenant/update-tenant.types';
import {
  BillingSewBillingTypeEnum,
  BillingSewRegionEnum,
} from '@/backoffice/types/types.generated';
import { SEW_BILLING_REGION_OPTIONS } from '@/reseller/sew/billing-regions/billing-regions.constants';
import {
  getSewBillingTypeOptions,
  getSewCostCenterHintSuffix,
  isSewCostCenterBillingType,
} from '@/reseller/sew/billing-types/billing-types.constants';
import { SEW_METADATA_KEYS } from '@/reseller/sew/metadata/sew-metadata.constants';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { FormContent } from '@/shared/components/ui/form/form-content';
import { FormContainer } from '@/shared/components/ui/form/form.styled';
import { GroupSelect } from '@/shared/features/groups/components/group-select/group-select';
import { getReseller } from '@/shared/utils/reseller/reseller';

export const UpdateTenantForm: React.FC<TUpdateTenantFormProps> = ({
  ownerOptions,
  handleClose,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TUpdateTenantFormSchema>();
  const isSew = getReseller() === 'sew';

  const [billingRegion, billingType] = useWatch({
    control,
    name: [
      `metaData.${SEW_METADATA_KEYS.billingRegion}`,
      `metaData.${SEW_METADATA_KEYS.billingType}`,
    ],
  });
  const isGermany = billingRegion === BillingSewRegionEnum.Germany;

  // Cost-center fields carry a type-specific hint; Customer Billing shows none.
  const hintSuffix = getSewCostCenterHintSuffix(billingType);

  // Billing Type only applies to Germany; for every other region the tenant is
  // always "Customer Billing".
  useEffect(() => {
    if (!isSew || isGermany) return;
    setValue(
      `metaData.${SEW_METADATA_KEYS.billingType}`,
      BillingSewBillingTypeEnum.CustomerBilling,
    );
  }, [isSew, isGermany, setValue]);

  return (
    <FormContainer justifyContent="space-between">
      <FormContent overflow="auto">
        <GroupSelect<TUpdateTenantFormSchema>
          name="groupId"
          getItemId={(value) => value}
          label={t('groups:group')}
        />
        <FormSelect<TUpdateTenantFormSchema>
          name="ownerId"
          label={t('common:owner')}
          options={ownerOptions}
          required
        />
        {isSew && (
          <>
            <Divider />
            <Typography variant="h7">
              {t('backoffice:sew.onboarding.billing_step')}
            </Typography>
            <FormSelect<TUpdateTenantFormSchema>
              name={`metaData.${SEW_METADATA_KEYS.billingRegion}`}
              label={t('backoffice:sew.onboarding.region')}
              required
              options={SEW_BILLING_REGION_OPTIONS}
            />
            {isGermany && (
              <FormSelect<TUpdateTenantFormSchema>
                name={`metaData.${SEW_METADATA_KEYS.billingType}`}
                label={t('backoffice:sew.onboarding.billing_type')}
                required
                defaultValue={BillingSewBillingTypeEnum.CustomerBilling}
                options={getSewBillingTypeOptions(t)}
              />
            )}
            <SewCustomerParties
              allowSeparateParties={!isSewCostCenterBillingType(billingType)}
              hintSuffix={hintSuffix}
            />
            <Divider />
          </>
        )}
      </FormContent>
      <Submit
        onClose={handleClose}
        submitButtonLabel={t('common:save_changes')}
        formHasToBeDirty
      />
    </FormContainer>
  );
};
